import { checkReturned, returnValue } from '../../components/utils/helper';
import { COREBASEURL } from '../../constants/global';
import {
	type IConsentLogData,
	type IConsentLogResponse,
	type IVerifyVolunteerAction,
	type IVolunteerStatusResponse,
} from '../../interface/IAgents';
import { agentType } from '../types';
import * as utils from './utils';

export const getAllAgentsAction = (pageSize?: number, pageNumber?: number, sort?: string) =>
	utils.httpGetMethod({
		apiData: {
			url: '',
			customurl:
				COREBASEURL +
				'party' +
				checkReturned(
					returnValue('pageSize=', pageSize) + returnValue('&pageNumber=', pageNumber) + returnValue('&sort=', sort)
				) +
				'&status=pending',
			header: utils.headerNoAuth(),
		},
		actionType: agentType.getAll,
	});

export interface ICreateAgentData {
	FirstName: string;
	MiddleName: string;
	LastName: string;
	Email: string;
	Mobile: string;
	Title: string;
	ProfileUrl: File | null;
	ElectionId: number;
	PollingUnitId: number;
	PartyId: number;
}

export const createAgentAction = (data: Record<string, any>, onSuccess?: (res: any) => void, onFailure?: (res: any) => void) =>
	utils.httpPostMethod({
		apiData: {
			url: '',
			customurl: COREBASEURL + 'Volunteer',
			header: utils.headerNoAuth(),
			data: JSON.stringify(data),
		},
		actionType: agentType.create,
		onSuccess,
		onFailure,
	});

export const getAgentProfile = (id: number) =>
	utils.httpGetMethod({
		apiData: {
			url: '',
			customurl: COREBASEURL + 'party',
			header: utils.headerNoAuth(),
		},
		actionType: agentType.getProfile,
	});

export const updateAgentAction = (id: number) =>
	utils.httpPutMethod({
		apiData: {
			url: '',
			customurl: COREBASEURL + 'party',
			header: utils.headerNoAuth(),
		},
		actionType: agentType.update,
	});

export const getPartyGuid = (guid: string) =>
	utils.httpGetMethod({
		apiData: {
			url: '',
			customurl: COREBASEURL + `Party/guid/${guid}`,
			header: utils.headerNoAuth(),
		},
		actionType: agentType.getPartyGuid,
	});

export const getElectionGuid = (guid: string) =>
	utils.httpGetMethod({
		apiData: {
			url: '',
			customurl: COREBASEURL + `Election/guid/${guid}`,
			header: utils.headerNoAuth(),
		},
		actionType: agentType.getElectionGuid,
	});

export const verifyVolunteerAction = (
	data: { email: string; phoneNumber: string; electionId: number },
	onSuccess?: (res: IVerifyVolunteerAction) => void,
	onFailure?: (err: any) => void
) =>
	utils.httpPostMethod({
		apiData: {
			url: '',
			customurl: COREBASEURL + 'Volunteer/application-exists',
			header: utils.headerNoAuth(),
			data: JSON.stringify(data),
		},
		actionType: agentType.verifyVolunteer,
		onSuccess,
		onFailure,
	});

// Anonymous status lookup for a just-submitted application. The endpoint uses an
// absolute route override on the backend (`/api/v1/volunteer-status`, not nested
// under `Volunteer`), which lines up with `COREBASEURL` already ending in
// `/api/v1/`. Body is a list of ids so the success page can poll its own record.
export const getVolunteerStatusAction = (
	volunteerIds: number[],
	onSuccess?: (res: IVolunteerStatusResponse) => void,
	onFailure?: (err: any) => void
) =>
	utils.httpPostMethod({
		apiData: {
			url: '',
			customurl: COREBASEURL + 'volunteer-status',
			header: utils.headerNoAuth(),
			data: JSON.stringify({ volunteerIds }),
		},
		actionType: agentType.volunteerStatus,
		onSuccess,
		onFailure,
	});

// Logs the volunteer's data-protection assent (GDPR/NDPR) for audit. Fired at
// consent time, before the volunteer record exists, so it is keyed on the
// verified phone number. The server stamps timestamp + IP and returns a
// consentReference that is later attached to the Volunteer create payload.
export const logConsentAction = (
	data: IConsentLogData,
	onSuccess?: (res: IConsentLogResponse) => void,
	onFailure?: (err: any) => void
) =>
	utils.httpPostMethod({
		apiData: {
			url: '',
			customurl: COREBASEURL + 'Volunteer/consent',
			header: utils.headerNoAuth(),
			data: JSON.stringify(data),
		},
		actionType: agentType.logConsent,
		onSuccess,
		onFailure,
	});
